<template>
  <!--
    This is intended to be a Singleton, hence the id.
    @todo Remove the id once we have migrated from Backbone.
  -->
  <ModalOverlay
    id="the-modal"
    :closeCallback="closeCallback"
    :handleAfterLeave="handleAfterLeave"
    :isDark="isDarkOverlay"
    :preventCloseOnOverlayClick="preventCloseOnOverlayClick"
    :visible="visible"
  >
    <div
      v-if="visible"
      :class="[$style.modal, {
        [$style.medium]: isMediumModal,
        [$style.small]: isSmallModal,
        [$style.large]: isLargeModal,
        [$style.fullScreen]: isFullScreenModal,
      }]"
    >
      <div
        :class="{
          [$style.modalContent]: true,
          [$style.modalContentOverflowVisible]: contentOverflowVisible
        }"
        @click.stop="() => {}"
      >
        <transition
          :enterActiveClass="$style.fadeTransitionDelay"
          :enterClass="$style.enterLeaveTransition"
          :leaveActiveClass="$style.fadeTransition"
          :leaveToClass="$style.enterLeaveTransition"
          mode="out-in"
        >
          <component
            :is="component"
            v-bind="componentProps"
          />
        </transition>
      </div>
    </div>
  </ModalOverlay>
</template>

<script>
import { get } from 'lodash';
import ConfirmationModal from 'client/components/Modal/ModalComponents/ConfirmationModal.vue';
import DeleteConfirmationNumberModal from 'client/components/Modal/DeleteConfirmationNumberModal.vue';
import DeviceEnableSystemInsightsModal from 'client/components/Modal/DeviceEnableSystemInsightsModal.vue';
import EmailChangeModal from 'client/components/Modal/EmailChangeModal';
import ForcePasswordChangeModal from 'client/systemusers/UserDetails/ForcePasswordChangeModal.vue';
import ImportFromCSVModal from 'client/systemusers/ImportUsers/ImportFromCSVModal.vue';
import LoggingService from 'client/util/LoggingService';
import ModalEventBus, { ModalEvents } from 'client/components/Modal/ModalEventBus';
import ModalOverlay from 'client/components/Modal/ModalComponents/ModalOverlay.vue';
import NoBillingPermissionsModal from 'client/components/MainHeader/NoBillingPermissionsModal.vue';
import OnlyAllowProviderPaymentsModal from 'client/components/MainHeader/OnlyAllowProviderPaymentsModal.vue';
import ProductTourModal from 'client/components/ProductTour/ProductTourModal.vue';
import RecapsModal from 'client/components/Modal/ModalComponents/RecapsModal.vue';
import ThankYouSubmitIdeaModal from 'client/Support/ThankYouSubmitIdeaModal.vue';
import TrialModal from 'client/components/Modal/TrialModal/TrialModal.vue';
import UpgradeModal from 'client/components/Modal/UpgradeModal.vue';

function isFunction(func) {
  return func && typeof func === 'function';
}

/**
 * Returns a fresh copy of TheModal default data so data from a previous
 * opening of TheModal isn't used on the next opening of TheModal. The fields
 * in the default data correspond to those that clients can specify in the
 * payload of the ModalEventBus.open() method or as arguments to $emit()
 * when emitting the open event.
 */
const getDefaultData = () => ({
  // If true then display a small-sized modal. TheModal will be medium by default.
  isSmallModal: false,

  // If true then display a medium-sized modal. TheModal will be medium by default.
  isMediumModal: false,

  // If true then display a large modal. TheModal will be medium by default.
  isLargeModal: false,

  // If true then display a full screen modal. TheModal will be medium by default.
  isFullScreenModal: false,

  // If true then sets the ModalOverlay to use the UI-Components default darker overlay
  // background color.
  isDarkOverlay: false,

  // The name or instance of the Vue component that will be displayed.
  component: undefined,

  // The props that will be passed to the displayed Vue component.
  componentProps: undefined,

  // If true then prevent clicks on the overlay from closing TheModal.
  preventCloseOnOverlayClick: false,

  // If true then prevents vue-date-picker from being cropped in PasswordConfirmationModal.
  // This prop is offered as a solution so as to avoid potentially breaking other modals.
  contentOverflowVisible: false,
});

/**
 * @description This is the only modal that should be used throughout the application. It can
 * be found in the top level app component where is it rendered to listen to events called
 * from the ModalEventBus.
 *
 * @example
 * // To launch from a _Vue_ component add the following import statement:
 * import ModalEventBus from 'client/components/Modal/ModalEventBus';
 *
 * @example
 * // To launch from a _Backbone_ component add the following require statement:
 * const ModalEventBus = require('client/components/Modal/ModalEventBus').default;
 *
 * Also make sure to add `.default` on the require statement for the component you want to use
 * in the modal e.g.
 * @example
 * const VueComponentName = require('client/components/VueComponentName.vue').default;
 *
 * @example
 * // Example event to render this modal component:
 * ModalEventBus.open({
 *   component: MyVueComponent,
 *   componentProps: {},
 * });
 *
 * @example
 * // Example event to render this modal component without the default close button:
 * ModalEventBus.open({
 *   component: MyVueComponent,
 *   componentProps: {},
 * });
 *
 * @example
 * // Example event to close the modal:
 * ModalEventBus.close();
 *
 * Do not render this component in more than one place in the application or multiple instances of
 * it will be rendered.
 */
export default {
  name: 'TheModal',

  components: {
    ConfirmationModal,
    DeleteConfirmationNumberModal,
    DeviceEnableSystemInsightsModal,
    EmailChangeModal,
    ForcePasswordChangeModal,
    ImportFromCSVModal,
    ModalOverlay,
    NoBillingPermissionsModal,
    OnlyAllowProviderPaymentsModal,
    ProductTourModal,
    RecapsModal,
    ThankYouSubmitIdeaModal,
    TrialModal,
    UpgradeModal,
  },

  /**
   * README regarding data and callbacks that go into <TheModal> & <ModalOverlay> components -
   * When calling ModalEventBus.open (the way to open a new instance of TheModal) you will have
   * to pass in a set of componentProps that allow you to extend the functionality of the modal.
   * An example of componentProps can be:
   * componentProps: {
   *   ...
   *   handleCancel: function,
   *   handleContinue: function,
   *   ...
   * };
   *
   * You can pass in custom callback functions for handleCancel and handleContinue.
   * These callbacks are added to the instance of the modal component in `renderNewComponent()`,
   * and then passed down as props to the ModalOverlay & slotted component.
   *
   * Since these custom callbacks are written in the calling/invoking component, we can
   * more granularly and easily define the UI/state response to the user's actions while
   * interacting with the modal.
   *
   * Also, take notice how many of the custom callbacks include a call to ModalEventBus[eventName].
   * (ModalEventBus.close). These events emit a value to TheModal component and allow us to hook
   * into those events. We create the event listeners for those events in `created()`.
   *
   * So, the flow of callbacks is as follows:
   *
   * Define custom handleCancel/Close callbacks in componentProps ->
   * Call ModalEventBus.open(), pass in componentProps ->
   * ModalEventBus.open emits "open" event to TheModal w/ componentProps in payload ->
   * TheModal catches "open" event, calls `renderNewComponent()`, adds data to component instance ->
   * DONE
   *
   * handleContinue callback ->
   *   passed down to slotted component ->
   *   slotted component implements callback ->
   *   when invoked, ModalEventBus.close() is called ->
   *   ModalEventBus.close() emits "close" event to TheModal ->
   *   TheModal catches event and sets `this.visible = false;` ->
   *   DONE
   *
   * handleCancel callback ->
   *   if passed in through componentProps ->
   *     register as part of component instance ->
   *     computed prop `handleCancel` gets set as custom handleCancel callback ->
   *     computed prop `closedCallback` gets set as custom handleCancel callback ->
   *     DONE
   *
   *   if not passed in through componentProps ->
   *     computed prop `handleCancel` gets set as undefined ->
   *     computed prop `closedCallback` gets set as existing handleClosed method ->
   *     DONE
   *
   *   computed prop `closeCallback` passed down as prop to ModalOverlay, called on overlay click ->
   *   DONE
   *
   * on ModalOverlay overlay click ->
   *   `closeCallback` prop is called ->
   *   depending on what `closeCallback` was set as during the previous steps ->
   *     if existing handleClose callback ->
   *       `this.visible = false;` ->
   *       DONE
   *
   *     if custom handleCancel callback ->
   *       custom handleCancel callback invoked ->
   *       `this.visible = false;` ->
   *       any other custom logic in custom callback also invoked ->
   *       DONE
   */

  data() {
    return {
      ...getDefaultData(),
      next: undefined,
      visible: false,
    };
  },

  computed: {
    /**
     * HY-1904:
     * Previously, clicking the overlay triggered the existing `handleClose`
     * method in this component. This kept us from informing the calling component
     * that the user decided to cancel the modal, essentially making the action "silent".
     * The calling component had no way of knowing that the user had done this.
     * So, we decided to instead give priority to the custom `handleCancel`
     * callback that could be passed in through `componentProps`.
     *
     * The custom `handleCancel` callback, which is defined in the calling component,
     * is able to define logic specific to that component, allowing us to dial in the UI
     * response to the user "cancelling" the modal.
     *
     * So, if the custom `handleCancel` callback was passed down in the
     * componentProps, use that instead of the existing handleClose
     * method. Then, we pass `closeCallback` down to <ModalOverlay> to allow
     * us to simplify props in child component/allow more control in parent.
     */
    closeCallback() {
      return this.handleCancel || this.handleClose;
    },

    handleCancel() {
      const handleCancel = get(this, 'componentProps.handleCancel');

      return isFunction(handleCancel) ? handleCancel : undefined;
    },
  },

  created() {
    // Register the event bus
    ModalEventBus.$on(ModalEvents.open, this.handleOpen);
    ModalEventBus.$on(ModalEvents.close, this.handleClose);
  },

  methods: {
    handleAfterLeave() {
      if (this.next) {
        ModalEventBus.open(this.next);
        return;
      }

      ModalEventBus.$emit(ModalEvents.transitionEnd);
    },

    handleClose() {
      LoggingService.debug('TheModal.handleClose');
      this.visible = false;
    },

    /**
     * @description This sets the dynamic component that is rendered when an
     * open event is emitted on the ModalEventBus.
     * @param payload Object with fields seen in getDefaultData() above.
     */
    handleOpen(payload = {}) {
      LoggingService.debug('TheModal.handleOpen: ', payload);

      this.renderNewComponent(payload);
      this.visible = true;

      LoggingService.debug('TheModal.handleOpen: next', this.next);

      this.next = payload.next;
    },

    /**
     * This method is called when a client calls the open() method or emits the
     * open event on the ModalEventBus. The payload passed to this method or
     * specified in the event data will be merged into a fresh copy of the
     * default data returned from the getDefaultData() method. This ensures
     * that each time TheModal is rendered it begins with a fresh copy of the
     * default data and with any settings specified by the client merged in.
     */
    renderNewComponent(payload = {}) {
      const newData = { ...getDefaultData(), ...payload };

      Object.keys(newData).forEach((key) => {
        this[key] = newData[key];
      });
    },
  },
};
</script>

<style module>
@import "./css/ModalStyles.css";

@value (max-width-medium, max-width-medium-large, max-width-large) from '~css/breakpoints.css';
@value (gradientDark1, gradientLight3) from '~css/colors.css';
@value (modalZIndex) from '~css/z-index.css';

.modal {
  border-radius: 50px;
  max-width: 600px;
  min-width: 500px;
  z-index: modalZIndex;
}

.small {
  max-width: 400px;
  min-width: 300px;
}

.medium {
  max-width: 900px;
  min-width: 750px;
}

.large {
  max-width: 1300px;
  min-width: auto;
  width: 65%;
}

.fullScreen {
  height: 100vh;
  max-width: 100%;
  min-width: 100vw;
}

@media (max-width: max-width-large) {
  .large { width: 80%; }
}

@media (max-width: max-width-medium-large) {
  .medium { min-width: auto; }

  .medium,
  .large {
    width: 90%;
  }
}

@media (max-width: max-width-medium) {
  .medium,
  .large {
    width: 100%;
  }
}

.modalContent {
  background: white;
  border-radius: 3px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.4);
  overflow: auto;
  position: relative;
  width: 100%;
}

.fullScreen .modalContent {
  max-height: 100vh;
}

.modalContentOverflowVisible {
  overflow: visible;
}

@media (max-width: max-width-medium) {
  .modalContent { border-radius: 0; }
}

/* Transitions */
.enterLeaveTransition {
  opacity: 0;
  transform: scale(0.95);
}

.fadeTransitionDelay {
  transition: opacity 0.3s ease-in-out 0.4s, transform 0.4s ease-out 0.4s;
}

.fadeTransition {
  transition: opacity 0.3s ease-in-out, transform 0.4s ease-out;
}
</style>
